import React, { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import DatePickerCalendar from './DatePickerCalendar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DateChecker = ({ id }) => {
  const [selectedDate, setSelectedDate] = useState('');
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);

  const checkDate = async () => {
    if (!selectedDate) return;
    setChecking(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/check-date`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: selectedDate }),
      });
      const data = await res.json();
      setResult(data);
    } catch {
      setResult({ status: 'error', message: 'Something went wrong. Please try again.' });
    } finally {
      setChecking(false);
    }
  };

  const statusColors = {
    available: 'bg-emerald-500/20 border-emerald-400 text-emerald-300',
    limited: 'bg-amber-500/20 border-amber-400 text-amber-300',
    unavailable: 'bg-red-500/20 border-red-400 text-red-300',
    error: 'bg-red-500/20 border-red-400 text-red-300',
  };

  return (
    <section id={id} data-testid="date-checker-section" className="py-16 sm:py-20" style={{ backgroundColor: '#1A1A1A' }}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
        <div className="text-center mb-8">
          <p className="text-gold text-sm font-semibold uppercase tracking-widest mb-3">The first step</p>
          <h2 className="font-serif text-3xl sm:text-4xl text-white mb-3">Check your date</h2>
          <p className="text-white/60 text-base">We only take a limited number of weddings each weekend.</p>
        </div>

        <div className="max-w-lg mx-auto">
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <p className="text-white/90 text-sm font-medium mb-4 uppercase tracking-wider text-center">
              Is your date still available?
            </p>
            <div className="flex gap-3">
              <DatePickerCalendar
                value={selectedDate}
                onChange={(d) => { setSelectedDate(d); setResult(null); }}
                className="flex-1"
              />
              <button
                data-testid="date-checker-btn"
                onClick={checkDate}
                disabled={!selectedDate || checking}
                className="bg-gold hover:bg-gold-light text-black font-semibold px-6 py-3.5 rounded-xl transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:hover:scale-100 flex items-center gap-2 whitespace-nowrap"
              >
                {checking ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    Checking...
                  </span>
                ) : (
                  <>Check <ChevronRight className="w-4 h-4" /></>
                )}
              </button>
            </div>

            {result && result.status && (
              <div className={`mt-4 p-4 rounded-xl border ${statusColors[result.status]} animate-fade-up`}>
                <p className="font-semibold text-sm">{result.message}</p>
                {result.urgency && <p className="text-xs mt-1 opacity-80">{result.urgency}</p>}
                <button
                  onClick={() => window.location.href = 'https://www.weddingvideo.co.uk/contact'}
                  className="mt-3 bg-gold hover:bg-gold-light text-black text-xs font-bold px-5 py-2 rounded-full transition-all hover:scale-105"
                >
                  {result.status === 'unavailable' ? 'Check Another Date' : 'Secure My Date'}
                </button>
              </div>
            )}
          </div>
          <p className="text-white/50 text-xs mt-3 text-center">No commitment. No spam. Just a quick check.</p>
        </div>
      </div>
    </section>
  );
};

export default DateChecker;
